import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
    Settings,
    LogOut,
    Bell,
    Search,
    Menu,
    X,
    ChevronDown,
    User
} from 'lucide-react';

const navItems = [
    { id: 'jobs', label: 'Jobs & Filter', icon: <Search className="w-5 h-5" /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell className="w-5 h-5" /> },
    { id: 'settings', label: 'Settings', icon: <Settings className="w-5 h-5" /> },
];

export default function DashboardLayout({ children, activeView, onViewChange }) {
    const { user, signOut } = useAuth();

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            {/* Top Navigation Bar */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
                <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-16">
                        {/* Left: Logo & Brand */}
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold shadow-sm shadow-blue-200">
                                M
                            </div>
                            <span className="font-bold text-gray-900 text-base sm:text-xl tracking-tight whitespace-nowrap">Mekko Job Notifier</span>
                        </div>

                        {/* Center: Navigation Links (Desktop) */}
                        <nav className="hidden md:flex space-x-1 items-center">
                            {navItems.flatMap((item) => {
                                if (item.id === 'settings') return [];
                                if (item.id === 'jobs') {
                                    return (
                                        <div key={item.id} className="relative group">
                                            <button
                                                type="button"
                                                onClick={() => { onViewChange(item.id); }}
                                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${activeView === item.id
                                                    ? 'bg-blue-50 text-blue-600 shadow-sm'
                                                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {item.icon}
                                                {item.label}
                                                <ChevronDown className="w-3 h-3 ml-1 text-gray-400 group-hover:text-gray-600" />
                                            </button>

                                            {/* Hover Dropdown */}
                                            <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 z-50">
                                                <button
                                                    type="button"
                                                    onClick={() => { onViewChange('saved-filters'); }}
                                                    className={`w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 flex items-center justify-between group/item ${activeView === 'saved-filters' ? 'bg-blue-50/50 text-blue-600' : ''}`}
                                                >
                                                    Job Filters
                                                    {activeView === 'saved-filters' && <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => { onViewChange('jobs'); }}
                                                    className={`w-full text-left px-4 py-2 text-sm ${activeView === 'jobs' ? 'text-blue-600 bg-blue-50/50' : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'} flex items-center justify-between font-medium`}
                                                >
                                                    Live Jobs
                                                    {activeView === 'jobs' && <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                }
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={() => { onViewChange(item.id); }}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${activeView === item.id
                                            ? 'bg-blue-50 text-blue-600 shadow-sm'
                                            : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                                            }`}
                                    >
                                        {item.icon}
                                        {item.label}
                                    </button>
                                );
                            })}
                        </nav>

                        {/* Right: User Profile & Actions */}
                        <div className="flex items-center gap-4">

                            {/* Profile Dropdown */}
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200"
                                >
                                    <User className="w-4 h-4 text-gray-500 sm:hidden" />
                                    <span className="truncate max-w-[160px] hidden sm:block">{user?.email}</span>
                                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isProfileMenuOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {isProfileMenuOpen && (
                                    <>
                                        {/* Backdrop to close */}
                                        <button type="button" aria-label="Close menu" className="fixed inset-0 z-10" onClick={() => setIsProfileMenuOpen(false)}></button>

                                        {/* Dropdown Menu */}
                                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-20 animate-in fade-in slide-in-from-top-2 duration-150">
                                            <div className="px-4 py-2 border-b border-gray-50 mb-1">
                                                <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">My Account</p>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => { onViewChange('settings'); setIsProfileMenuOpen(false); }}
                                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                                            >
                                                <User className="w-4 h-4 text-gray-400" />
                                                Profile Settings
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => { onViewChange('settings'); setIsProfileMenuOpen(false); }}
                                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                                            >
                                                <Settings className="w-4 h-4 text-gray-400" />
                                                Account Settings
                                            </button>

                                            <div className="my-1 border-t border-gray-100"></div>

                                            <button
                                                type="button"
                                                onClick={signOut}
                                                className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors group"
                                            >
                                                <LogOut className="w-4 h-4 text-red-400 group-hover:text-red-500" />
                                                Log Out
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Mobile Menu Button */}
                            <button type="button" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
                                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Navigation Menu */}
                {isMobileMenuOpen && (
                    <div className="md:hidden border-t border-gray-100 bg-white">
                        <div className="px-4 pt-2 pb-4 space-y-1">
                            {navItems.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => { onViewChange(item.id); setIsMobileMenuOpen(false); }}
                                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-base font-medium ${activeView === item.id
                                        ? 'bg-blue-50 text-blue-600'
                                        : 'text-gray-600 hover:bg-gray-50'
                                        }`}
                                >
                                    {item.icon}
                                    {item.label}
                                </button>
                            ))}
                            <div className="border-t border-gray-100 my-2 pt-2">
                                <button type="button" onClick={signOut} className="w-full flex items-center gap-3 px-3 py-3 text-red-600 font-medium rounded-lg hover:bg-red-50">
                                    <LogOut className="w-5 h-5" /> Sign Out
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </header>

            {/* Main Content Area */}
            <main className="flex-1 overflow-auto bg-gray-50 relative p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto w-full">
                {children}
            </main>


        </div>
    );
}
